import { useState } from "react";
import { createPortal } from "react-dom";

interface Props {
    items?: any[];
    imageProperty?: string;
    titleProperty?: string;
    getKey?: (item: any, index: number) => string | number;
    getSubtitle?: (item: any) => string;
    onAdd?: () => void;
    onEdit?: (item: any) => void;
    onDelete?: (item: any) => void;
    canAdd?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    addLabel?: string;
    emptyMessage?: string;
}

export const ImageGrid = ({
    items = [],
    imageProperty = "ImageURL",
    titleProperty = "Name",
    getKey,
    getSubtitle,
    onAdd,
    onEdit,
    onDelete,
    canAdd = true,
    canEdit = true,
    canDelete = true,
    addLabel = "Agregar imágenes",
    emptyMessage = "No hay imágenes para mostrar",
}: Props) => {
    const [preview, setPreview] = useState<any>(null);

    const getData = (item: any) => item?.DataBeanProperties ?? item;
    const canShowActions = (canEdit && onEdit) || (canDelete && onDelete);

    const previewModal = preview && createPortal(
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 10000, background: "rgba(0, 0, 0, .92)", padding: "32px" }} onClick={() => setPreview(null)} >
            <button
                type="button"
                className="btn btn-light btn-icon position-fixed"
                style={{ top: "16px", right: "20px", zIndex: 10001 }}
                aria-label="Cerrar vista previa"
                onClick={() => setPreview(null)}
            >
                <i className="ri-close-line fs-4"></i>
            </button>

            <div className="text-center" onClick={(event) => event.stopPropagation()}>
                <img
                    src={preview.url}
                    alt={preview.name}
                    style={{ maxWidth: "94vw", maxHeight: "84vh", objectFit: "contain" }}
                />
                <div className="text-white mt-3 d-flex align-items-center justify-content-center gap-3">
                    <span>{preview.name}</span>
                    <a
                        href={preview.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={preview.name}
                        className="btn btn-sm btn-light"
                    >
                        <i className="ri-download-2-line me-1"></i>
                        Descargar
                    </a>
                </div>
            </div>
        </div>,
        document.body
    );

    return (
        <>
            <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                <span className="text-muted">
                    {items.length} {items.length === 1 ? "imagen" : "imágenes"}
                </span>

                {canAdd && onAdd && (
                    <button type="button" className="btn btn-primary" onClick={onAdd}>
                        <i className="ri-image-add-line me-1"></i>
                        {addLabel}
                    </button>
                )}
            </div>

            {items.length === 0 ? (
                <div className="border rounded text-center text-muted py-5">
                    <i className="ri-image-line d-block mb-2" style={{ fontSize: "48px" }}></i>
                    {emptyMessage}
                </div>
            ) : (
                <div className="row g-3 align-items-start">
                    {items.map((item, index) => {
                        const data = getData(item);
                        const imageUrl = data?.[imageProperty];
                        const name = data?.[titleProperty] ?? "Imagen sin nombre";
                        const subtitle = getSubtitle?.(data);

                        return (
                            <div
                                key={getKey?.(data, index) ?? data?.ID ?? index}
                                className="col-12 col-sm-6 col-md-4"
                            >
                                <div className="card border shadow-sm mb-0 overflow-hidden h-100">
                                    <button
                                        type="button"
                                        className="border-0 p-0 bg-light position-relative w-100"
                                        style={{
                                            aspectRatio: "1 / 1",
                                            overflow: "hidden",
                                            cursor: imageUrl ? "zoom-in" : "default",
                                        }}
                                        disabled={!imageUrl}
                                        onClick={() => imageUrl && setPreview({ url: imageUrl, name })}
                                    >
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={name}
                                                loading="lazy"
                                                className="position-absolute top-0 start-0 w-100 h-100"
                                                style={{ objectFit: "cover" }}
                                            />
                                        ) : (
                                            <span className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                                                <i className="ri-image-line fs-1"></i>
                                                Sin imagen
                                            </span>
                                        )}
                                    </button>

                                    <div className="card-body p-3 d-flex align-items-start gap-2">
                                        <div className="flex-grow-1 overflow-hidden">
                                            <div className="fw-semibold text-truncate" title={name}>{name}</div>
                                            {subtitle && <div className="text-muted small text-truncate">{subtitle}</div>}
                                        </div>

                                        {imageUrl && (
                                            <a
                                                href={imageUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                download={name}
                                                title="Descargar"
                                                className="btn btn-sm btn-light btn-icon flex-shrink-0"
                                            >
                                                <i className="ri-download-2-line"></i>
                                            </a>
                                        )}

                                        {canShowActions && (
                                            <div className="dropdown flex-shrink-0">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light btn-icon"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                    title="Opciones"
                                                >
                                                    <i className="ri-more-2-fill"></i>
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-end">
                                                    {canEdit && onEdit && (
                                                        <li>
                                                            <button className="dropdown-item" type="button" onClick={() => onEdit(data)}>
                                                                <i className="ri-edit-2-line me-2"></i>
                                                                Editar
                                                            </button>
                                                        </li>
                                                    )}
                                                    {canDelete && onDelete && (
                                                        <li>
                                                            <button className="dropdown-item text-danger" type="button" onClick={() => onDelete(data)}>
                                                                <i className="ri-delete-bin-line me-2"></i>
                                                                Eliminar
                                                            </button>
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {previewModal}
        </>
    );
};
